import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import CalendarDatePicker from '@components/picker/CalendarDatePicker'
import Tag from '@components/tag/Tag'

import ManifestationFilter from './ControleManifestationFilter'
import MetierFilter from '@components/filter/MetierFilter'
import SocieteFilter from '@components/filter/SocieteFilter'
import ControleTypeFilter from './ControleTypeFilter'

import FilterLayout from '@components/filter/FilterLayout'
import GroupedFilterLayout from './GroupedFilterLayout'

import useReducer from '@store/useReducer'
import * as FiltreStore from '@store/filtre/filtre'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

interface Props {
  filtres: ControleFilters
  onFiltre: (part: Partial<ControleFilters>) => void
  onReset: () => void
  display: (keyof ControleFilters)[]
}

const PERIODS = ['all', 'year', 'halfYear', 'month'] as const

const ControleFilters = ({ filtres, onFiltre, onReset, display }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const metiers = useReducer(FiltreStore.store, (s) => s.metiers)
  const manifestations = useReducer(FiltreStore.store, (s) => s.manifestations)
  const societes = useReducer(FiltreStore.store, (s) => s.societes)

  const [open, setOpen] = React.useState<'manifestation' | 'metier' | 'societe' | 'type'>()

  if (bp === 'phone') {
    return <GroupedFilterLayout display={display} filtres={filtres} onFiltre={onFiltre} onReset={onReset} />
  }

  return (
    <MainContainer>
      <Line>
        {/* MANIFESTATION */}
        {display.includes('manifestation') && (
          <FilterContainer idx={0}>
            <FilterLayout
              open={open === 'manifestation'}
              onOpen={(value) => setOpen(value ? 'manifestation' : undefined)}
              name={`${i18n.t('filters.tags.manifestation')}${filtres.manifestation.length || ''}`}
              active={filtres.manifestation.length > 0}>
              <ManifestationFilter
                manifestations={manifestations}
                onManifestationsSelected={(values) => {
                  onFiltre({ manifestation: values })
                  setOpen(undefined)
                }}
                selected={filtres.manifestation}
              />
            </FilterLayout>
          </FilterContainer>
        )}

        {/* METIER */}
        {display.includes('metier') && (
          <FilterContainer idx={1}>
            <FilterLayout
              open={open === 'metier'}
              onOpen={(value) => setOpen(value ? 'metier' : undefined)}
              name={`${i18n.t('filters.tags.metier')}${filtres.metier.length || ''}`}
              active={filtres.metier.length > 0}>
              <MetierFilter
                metiers={metiers}
                onMetiersSelected={(values) => {
                  onFiltre({ metier: values })
                  setOpen(undefined)
                }}
                selected={filtres.metier}
              />
            </FilterLayout>
          </FilterContainer>
        )}

        {/* TYPE */}
        {display.includes('type') && (
          <FilterContainer idx={2}>
            <FilterLayout
              open={open === 'type'}
              onOpen={(value) => setOpen(value ? 'type' : undefined)}
              name={filtres.type ? i18n.t(`filters.controle.type.${filtres.type}`) : i18n.t('filters.tags.type')}
              active={!!filtres.type}>
              <ControleTypeFilter
                onTypeSelected={(value) => {
                  onFiltre({ type: value })
                  setOpen(undefined)
                }}
                selected={filtres.type}
              />
            </FilterLayout>
          </FilterContainer>
        )}

        {/* SOCIETE */}
        {display.includes('societe') && (
          <FilterContainer idx={3}>
            <FilterLayout
              open={open === 'societe'}
              onOpen={(value) => setOpen(value ? 'societe' : undefined)}
              name={`${i18n.t('filters.tags.societe')}${filtres.societe.length || ''}`}
              active={filtres.societe.length > 0}>
              <SocieteFilter
                societes={societes}
                onSocietesSelected={(values) => {
                  onFiltre({ societe: values })
                  setOpen(undefined)
                }}
                selected={filtres.societe}
              />
            </FilterLayout>
          </FilterContainer>
        )}

        {/* DATE */}
        {display.includes('from') && display.includes('to') && (
          <FilterContainer idx={4}>
            <Tag
              item={{
                label:
                  filtres.from && filtres.to
                    ? i18n.t('filters.tags.range', { from: new Date(filtres.from), to: new Date(filtres.to) })
                    : i18n.t('filters.tags.date'),
                value: !!filtres.to,
              }}
              onItemSelected={() =>
                CalendarDatePicker.open({
                  mode: 'popup',
                  onDates: (from, to) =>
                    onFiltre({
                      from: from ? from.toISOString() : undefined,
                      to: to ? to.toISOString() : undefined,
                      period: !from && !to ? 'all' : undefined,
                    }),
                  start: filtres.from ? new Date(filtres.from) : undefined,
                  end: filtres.to ? new Date(filtres.to) : undefined,
                  max: new Date(),
                  required: false,
                })
              }
              selected={!!filtres.to}
              inactiveColor
            />
          </FilterContainer>
        )}

        {/* PERIOD */}
        {display.includes('period') &&
          PERIODS.map((item, index) => (
            <FilterContainer key={item} idx={5 + index}>
              <Tag
                item={{ label: i18n.t(`screens.controleDetail.sort.${item}`), value: item }}
                onItemSelected={() =>
                  onFiltre({
                    from: undefined,
                    to: undefined,
                    period: item,
                  })
                }
                selected={filtres.period === item}
                inactiveColor
              />
            </FilterContainer>
          ))}

        <ResetText onPress={onReset}>{i18n.t('common.reset')}</ResetText>
      </Line>
    </MainContainer>
  )
}

export default ControleFilters

const MainContainer = styled(View)`
  margin-top: 24px;
  flex-direction: row;
  justify-content: space-between;
`

const Line = styled(View)`
  flex-flow: row wrap;
  align-items: center;
  flex: 1;
  margin-right: 40px;
`

const ResetText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: 16px;
  text-decoration: underline;
  margin: 16px 0;
  z-index: -10;
`

const FilterContainer = styled(View)<{ idx: number }>`
  margin: 8px 0;
  margin-right: 8px;
  z-index: -${(props) => props.idx};
`
